import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import imputableService, { ImputableCRUD } from '../../services/imputableService';
import * as XLSX from 'xlsx';

const Imputables: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [imputables, setImputables] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchImputables = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await imputableService.getImputablesPaged({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setImputables(result.items);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      console.error('Error al cargar imputables:', error);
      enqueueSnackbar('Error al cargar imputables', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImputables();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchImputables(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchImputables(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchImputables(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (codImputable: string) => {
    if (window.confirm('¿Está seguro de eliminar este imputable?')) {
      try {
        await imputableService.deleteImputable(codImputable);
        enqueueSnackbar('Imputable eliminado exitosamente', { variant: 'success' });
        fetchImputables();
      } catch (error: any) {
        console.error('Error al eliminar imputable:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar imputable', { variant: 'error' });
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const result = await imputableService.getImputablesPaged({ pageNumber: 1, pageSize: 10000, searchTerm: '' });
      const dataToExport = result.items.map((item: any) => ({
        'Código': item.codImputable,
        'Nombre': item.nombre,
        'Nivel': item.nivel,
        'Imputable': item.imputable === 'S' ? 'Sí' : 'No',
        'Tipo': item.tipo,
        'Activo': item.activo === 'S' ? 'Sí' : 'No'
      }));
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Imputables');
      XLSX.writeFile(wb, 'Imputables.xlsx');
      enqueueSnackbar('Archivo exportado exitosamente', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar:', error);
      enqueueSnackbar('Error al exportar datos', { variant: 'error' });
    }
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        for (const row of data) {
          const imputable = {
            codImputable: (row as any)['Código'],
            nombre: (row as any)['Nombre'],
            nivel: (row as any)['Nivel'],
            imputable: (row as any)['Imputable'] === 'Sí' ? 'S' : 'N',
            tipo: (row as any)['Tipo'],
            activo: (row as any)['Activo'] === 'Sí' ? 'S' : 'N'
          };
          await imputableService.createImputable(imputable);
        }

        enqueueSnackbar('Datos importados exitosamente', { variant: 'success' });
        fetchImputables();
      } catch (error) {
        console.error('Error al importar:', error);
        enqueueSnackbar('Error al importar datos', { variant: 'error' });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Imputables
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" color="primary" startIcon={<FileDownloadIcon />} onClick={handleExportExcel}>
                Exportar
              </Button>
              <Button variant="outlined" color="primary" component="label" startIcon={<FileUploadIcon />}>
                Importar
                <input type="file" hidden accept=".xlsx,.xls" onChange={handleImportExcel} />
              </Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/contabilidad/imputables/nuevo')}>
                Nuevo Imputable
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {imputables.map((imputable) => (
                <Card key={imputable.codImputable} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {imputable.codImputable} - {imputable.nombre}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nivel: {imputable.nivel} | Tipo: {imputable.tipo}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/contabilidad/imputables/${imputable.codImputable}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(imputable.codImputable)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nivel</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Imputable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Activo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imputables.map((imputable) => (
                    <TableRow key={imputable.codImputable} hover>
                      <TableCell>{imputable.codImputable}</TableCell>
                      <TableCell>{imputable.nombre}</TableCell>
                      <TableCell>{imputable.nivel}</TableCell>
                      <TableCell>{imputable.imputable === 'S' ? 'Sí' : 'No'}</TableCell>
                      <TableCell>{imputable.tipo}</TableCell>
                      <TableCell>{imputable.activo === 'S' ? 'Sí' : 'No'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/contabilidad/imputables/${imputable.codImputable}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(imputable.codImputable)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Imputables;
